<template>
    <div>
        <div class="pb-2">
            <v-select
                v-model="selectedAccessor"
                :multiple="true"
                :options="authorAccessorOptions"
                :close-on-select="true"
                label="accessor_id"
                class="mw-100 mb-2"
                placeholder="Add authors or teams..."
                :get-option-key="(a) => a.accessor_id"
                :filter-by="filterAccessors"
                :disabled="disabled || isReadOnly"
                @option:selected="createAccessor($event)"
                ref="selector"
            >
                <template slot="option" slot-scope="option">
                    <div class="d-flex align-items-center py-1">
                        <div class="flex-grow-1 pr-2">
                            <div v-if="option.accessor_type == 'individual'">
                                <div
                                    v-if="option.organization_member.full_name"
                                    class="small font-weight-500"
                                    style="margin-bottom: 2px"
                                >
                                    {{ option.organization_member.full_name }}
                                </div>
                                <div class="small text-secondary">
                                    {{ option.organization_member.user.email }}
                                </div>
                            </div>
                            <div v-else class="small font-weight-500">
                                <span class="icon text-secondary icon-glyphicons-basic-user-group mr-2"></span>
                                <span>{{ option.group.name }}</span>
                            </div>
                        </div>
                        <div class="flex-grow-0 small">
                            <span v-if="option.accessor_type == 'individual'" class="badge badge-gray text-uppercase">{{
                                getRoleName(option.organization_member.role)
                            }}</span>
                            <span v-else class="badge badge-gray">TEAM</span>
                        </div>
                    </div>
                </template>
                <template v-slot:selected-option="option">
                    <div v-if="option.access_type == 'individual'">
                        <div
                            v-if="option.organization_member.full_name"
                            class="small font-weight-500 text-secondary"
                            style="line-height: 1"
                        >
                            {{ option.organization_member.full_name }}
                        </div>
                        <div class="small">
                            {{ option.organization_member.user.email }}
                        </div>
                    </div>
                    <div v-else>
                        <span class="icon text-secondary icon-glyphicons-basic-user-group mr-2"></span>
                        <span class="medium">{{ option.group.name }}</span>
                    </div>
                </template>
                <template slot="no-options">
                    <div class="py-2">
                        <span class="text-secondary">No teams found.</span>
                    </div>
                </template>
            </v-select>
        </div>
        <!-- List of Accessors -->
        <div class="mt-3">
            <div v-for="(accessor, index) in accessors.direct" :key="accessor.id">
                <div class="d-flex align-items-center accessor-container">
                    <div v-if="accessor.accessor_type === 'individual'" class="flex-grow-1">
                        <div class="small font-weight-500">
                            <span class="mr-2 align-middle">{{ getAccessorName(accessor) }}</span>
                            <span class="badge badge-gray text-uppercase align-middle">{{
                                accessor.organization_member.role
                            }}</span>
                        </div>
                        <div v-if="accessor.organization_member.full_name" class="small text-secondary pt-1">
                            {{ accessor.organization_member.user.email }}
                            <span v-if="!accessor.organization_member.is_active" class=""> (deactivated) </span>
                        </div>
                    </div>
                    <div v-else class="flex-grow-1">
                        <div class="small font-weight-500">
                            <span
                                class="icon icon-glyphicons-basic-user-group text-secondary mr-2 ml-1 align-middle"
                            ></span>
                            <span class="mr-2 align-middle">{{ accessor.group.name }}</span>
                            <span class="badge badge-gray text-uppercase align-middle">Team</span>
                        </div>
                    </div>
                    <div v-if="accessor.isLoading">
                        <span class="mx-3">
                            <span
                                class="spinner-border spinner-border-sm text-primary"
                                role="status"
                                aria-hidden="true"
                            ></span>
                            <span class="sr-only">Loading...</span>
                        </span>
                    </div>
                    <div v-else class="dropdown">
                        <button
                            class="btn btn-gray dropdown-toggle badge badge-gray badge-small"
                            type="button"
                            aria-expanded="false"
                            data-toggle="dropdown"
                            :disabled="isReadOnly"
                        >
                            {{ getAccessTypeName(accessor.access_type) }}
                        </button>
                        <div class="dropdown-menu dropdown-menu-right medium shadow-sm">
                            <h6 class="dropdown-header">Role</h6>
                            <button
                                class="dropdown-item align-middle py-2"
                                :class="{ active: accessor.access_type === accessType.value }"
                                @click="updateAccessType(accessor, accessType.value)"
                                v-for="accessType in accessTypeOptions"
                                :key="accessType.value"
                            >
                                <div>
                                    <span class="mr-1 align-middle">{{ accessType.label }}</span>
                                    <span
                                        v-if="accessor.access_type === accessType.value"
                                        class="icon icon-100 icon-glyphicons-basic-check align-middle"
                                    ></span>
                                </div>
                                <div class="text-secondary small">{{ accessType.description }}</div>
                            </button>
                            <div class="dropdown-divider"></div>
                            <button class="dropdown-item" @click="removeAccessor(accessor, index)">
                                <span class="icon icon-100 icon-glyphicons-basic-bin mr-1 align-middle"></span>
                                <span class="align-middle">Remove Access</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <hr v-show="accessors.direct.length" />
            <div class="medium mb-2" v-show="inheritedAccessorListCombined.length">
                The following authors and teams have inherited access from somewhere else and can't be edited here.
            </div>
            <div v-for="accessor in inheritedAccessorListCombined" :key="accessor.accessor_id">
                <div class="d-flex align-items-start accessor-container">
                    <div v-if="accessor.accessor_type === 'individual'" class="flex-grow-1">
                        <div class="small font-weight-500">
                            <span class="mr-2 align-middle">{{ getAccessorName(accessor) }}</span>
                            <span class="badge badge-gray text-uppercase align-middle">{{
                                getAccessorRole(accessor)
                            }}</span>
                        </div>
                        <div v-if="accessor.organization_member.full_name" class="small text-secondary pt-1">
                            {{ accessor.organization_member.user.email }}
                        </div>
                    </div>
                    <div v-else class="flex-grow-1">
                        <div class="small font-weight-500">
                            <span
                                class="icon icon-glyphicons-basic-user-group text-secondary mr-2 ml-1 align-middle"
                            ></span>
                            <span class="mr-2 align-middle">{{ accessor.group.name }}</span>
                            <span class="badge badge-gray text-uppercase align-middle">Team</span>
                        </div>
                    </div>
                    <div class="flex-grow-0">
                        <button
                            class="btn btn-gray dropdown-toggle badge badge-gray badge-small align-middle"
                            type="button"
                            disabled
                        >
                            <span v-if="accessor.type === 'admin'">Owner</span>
                            <span v-else>{{ getAccessTypeName(accessor.access_type) }}</span>
                        </button>
                    </div>
                </div>
            </div>
            <div v-if="userIsOrganizationAdmin" class="small pt-3 pb-1 d-flex justify-content-end">
                <a :href="usersViewUrl" target="_blank">
                    <span class="mr-1">Manage Authors & Teams</span>
                    <span class="icon icon-glyphicons-basic-square-new-window"></span>
                </a>
            </div>
        </div>
    </div>
</template>

<script>
import http from "@/axios.js"
import { roleNames } from "@/assets/js/settings.js"

export default {
    name: "LibraryItemAccess",
    props: {
        // The library item for which access is being edited.
        value: {
            type: Object,
            required: true,
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            selectedAccessor: null,
            authorGroups: [],
            authorIndividuals: [],
            accessors: {
                direct: [],
                inherited: [],
                admins: [],
            },
            isReadOnly: false,
            accessTypes: {
                view: "Viewer",
                edit: "Editor",
                own: "Owner",
            },
            accessTypeOptions: [
                {
                    value: "view",
                    label: "Viewer",
                    description: "Ability to view and clone.",
                },
                {
                    value: "edit",
                    label: "Editor",
                    description: "Ability to edit contents.",
                },
                {
                    value: "own",
                    label: "Owner",
                    description: "Ability to edit, manage access, and move.",
                },
            ],
            isLoading: false,
        }
    },
    computed: {
        // The combined list of group-type and individual-type accessors.
        authorAccessorOptions() {
            let authorGroups = this.authorGroups.map((a) => {
                return {
                    group: {
                        ...a,
                    },
                    accessor_id: `group-${a.id}`,
                    accessor_type: "group",
                    label: a.name,
                }
            })
            let authorIndividuals = this.authorIndividuals.map((a) => {
                return {
                    organization_member: {
                        ...a,
                    },
                    accessor_id: `individual-${a.id}`,
                    accessor_type: "individual",
                    label: a.email,
                }
            })
            return authorGroups.concat(authorIndividuals)
        },
        userIsOrganizationAdmin() {
            return this.$store.getters.userIsOrganizationAdmin
        },
        organizationId() {
            return this.$store.getters.currentOrganizationId
        },
        usersViewUrl() {
            return this.$router.resolve({ name: "MemberList" }).href
        },
        inheritedAccessorListCombined() {
            let inherited = this.accessors.inherited.map((a) => {
                return {
                    type: "inherited",
                    ...a,
                }
            })
            let admins = this.accessors.admins.map((a) => {
                return {
                    type: "admin",
                    accessor_type: "individual",
                    organization_member: a,
                }
            })
            return inherited.concat(admins)
        },
    },
    watch: {
        value: function () {
            this.fetchData()
        },
        isLoading: function (newValue) {
            this.$emit("update:loading", newValue)
        },
    },
    methods: {
        fetchData() {
            this.isLoading = true
            let vm = this
            Promise.all([this.fetchAuthorGroups(), this.fetchCreators(), this.fetchAccessorList()]).finally(() => {
                vm.isLoading = false
            })
        },
        fetchAccessorList() {
            let vm = this
            return http.get(`/api/library-items/${this.value.id}/access/`).then((response) => {
                vm.accessors = response.data.accessors
                vm.isReadOnly = response.data.is_read_only
            })
        },
        // Fetch all authoring groups.
        fetchAuthorGroups() {
            let vm = this
            return new Promise((resolve) => {
                http.get("/api/groups/").then(function (response) {
                    vm.authorGroups = response.data
                    resolve()
                })
            })
        },
        // Fetch all author org members.
        fetchCreators() {
            let vm = this
            return new Promise((resolve) => {
                http.post("/api/members/search/", {
                    query: "",
                    start: 0,
                    end: 100, // Set this to a high value to fetch all authors.
                    sort: "user.email",
                    sortDescend: false,
                    organizationID: vm.organizationId,
                    filters: {
                        role: {
                            value: "creator",
                        },
                        status: {
                            value: "pending,active",
                        },
                    },
                }).then(function (response) {
                    vm.authorIndividuals = response.data.members
                    resolve()
                })
            })
        },
        // Callback used the select component to filter results based on the search query.
        filterAccessors(option, label, search) {
            let lowerCaseSearch = search.toLowerCase()
            if (option.accessor_type == "individual") {
                let nameMatch = option.organization_member.full_name.toLowerCase().includes(lowerCaseSearch)
                let emailMatch = option.organization_member.user.email.toLowerCase().includes(lowerCaseSearch)
                return nameMatch || emailMatch
            } else {
                return option.group.name.toLowerCase().includes(lowerCaseSearch)
            }
        },
        // Convert a role into a display-friendly name.
        getRoleName(role) {
            return roleNames[role]
        },
        // Get a display name for an accessor - use name, fall back to email
        getAccessorName(accessor) {
            return accessor.organization_member.full_name || accessor.organization_member.user.email
        },
        getAccessorRole(accessor) {
            if (accessor.organization_member.user.is_application_admin) {
                return "Affinity Team"
            }
            return accessor.organization_member.role
        },
        getAccessTypeName(access_type) {
            return this.accessTypes[access_type]
        },
        createAccessor(selection) {
            let newAccessor = selection[0]

            // Clear the current selector.
            this.selectedAccessor = null

            // Make sure the group or individual isn't already added as a direct accessor.
            if (newAccessor.accessor_type === "individual") {
                let orgMemberIds = this.accessors.direct
                    .filter((a) => a.accessor_type === "individual")
                    .map((a) => a.organization_member.id)
                if (orgMemberIds.includes(newAccessor.organization_member.id)) {
                    return
                }
            } else {
                let groupIds = this.accessors.direct.filter((a) => a.accessor_type === "group").map((a) => a.group.id)
                if (groupIds.includes(newAccessor.group.id)) {
                    return
                }
            }

            // Format the new accessor for saving.
            if (newAccessor.organization_member) {
                newAccessor.organizationMemberId = newAccessor.organization_member.id
            }
            if (newAccessor.group) {
                newAccessor.groupId = newAccessor.group.id
            }

            this.$set(newAccessor, "isLoading", true)
            // Add the accessor to the list of displayed accessor.
            this.accessors.direct.unshift(newAccessor)
            let vm = this
            http.post(`/api/library-items/${this.value.id}/access/`, newAccessor).then((response) => {
                vm.$set(newAccessor, "id", response.data.id)
                vm.$set(newAccessor, "accessor_id", response.data.accessor_id)
                vm.$set(newAccessor, "access_type", response.data.access_type)
                newAccessor.isLoading = false
            })
        },
        removeAccessor(accessor, index) {
            // Remove the accessor from the list and persist the change in the background.
            this.accessors.direct.splice(index, 1)
            http.put(`/api/access/${accessor.id}/remove/`)
        },
        updateAccessType(accessor, accessType) {
            // Update the accessor role and persist the change in the background.
            this.$set(accessor, "access_type", accessType)
            http.put(`/api/access/${accessor.id}/`, {
                access_type: accessType,
            })
        },
    },
    created() {
        this.fetchData()
    },
}
</script>

<style lang="scss" scoped>
.accessor-container {
    margin-top: 20px;
    margin-bottom: 20px;
}
</style>